import { GeoLocation } from '../location.types';
import { GEO_ADDRESS_COMPONENT_TYPES, GEO_LOCATION_TYPES, GeoCoordinates, PERMISSION_STATE, } from './types';

export const hasUnnamedRoad = /[U-u]nnamed\s?[R-r]oad.?\s?/g
export const hasNumericRange = /^\s*[\d]+\s*-\s*[\d]+/
export const hasHighwayName = /\b(?:I[-\s]?\d{1,3}|US[-\s]?\d{1,3}|SR[-\s]?\d{1,3}|State[-\s]?\d{1,3}|County[-\s]?\d{1,3}|[A-Z]{1,2}[-\s]?\d{1,3}|(?:\w+\s+)?(?:Parkway|Pkwy|Highway|Hway|Hwy|Route|Rte|Rt|Expressway|Expy|Expwy|Exp|Turnpike|Tpke|Trnpk|Interstate|Int|Freeway|Fwy|Spur|Loop))\b/i

const getAddressComponentByType = (
  location: GeoLocation,
  type: GEO_ADDRESS_COMPONENT_TYPES
): GeoLocation['address_components'][0] =>
  location?.address_components.find(
    (addressComponent) => addressComponent.types.indexOf(type) > -1
  )

export const getLocationsByType = (
  allLocations: Array<GeoLocation>,
  type: GEO_LOCATION_TYPES
): Array<GeoLocation> =>
  allLocations.filter((result) => result.geometry.location_type === type)

const getNearSameNameRoute = (
  allLocations: Array<GeoLocation>,
  routeComponent: GeoLocation['address_components'][0],
  locationType: GEO_LOCATION_TYPES
): GeoLocation => {
  const nearLocations = getLocationsByType(allLocations, locationType)
  const nearSameNameRoute  = nearLocations.find((location) => {
    const nearestRouteComponent = getAddressComponentByType(
      location,
      GEO_ADDRESS_COMPONENT_TYPES.ROUTE
    )

    return (
      nearestRouteComponent?.short_name.toLowerCase() ===
      routeComponent.short_name.toLowerCase()
    )
  })

  return nearSameNameRoute
}

export const getRecommendedLocation = (
  allLocations: Array<GeoLocation>,
  preferredType: GEO_LOCATION_TYPES
) => {
  const locationsByType = getLocationsByType(allLocations, preferredType)
    .filter((recommendedLocation) => getAddressComponentByType(
      recommendedLocation,
      GEO_ADDRESS_COMPONENT_TYPES.ROUTE
    ))

  return (
    locationsByType.find((location) =>
      hasHighwayName.test(
        getAddressComponentByType(
          location,
          GEO_ADDRESS_COMPONENT_TYPES.ROUTE
        )?.short_name
      )
    ) || locationsByType[0]
  )
}

export const guessNearestLocation = (
  allLocations: Array<GeoLocation>,
  recommendedLocation: GeoLocation,
): GeoLocation => {
  const streetComponent = getAddressComponentByType(
    recommendedLocation,
    GEO_ADDRESS_COMPONENT_TYPES.STREET_NUMBER
  )
  const routeComponent = getAddressComponentByType(
    recommendedLocation,
    GEO_ADDRESS_COMPONENT_TYPES.ROUTE
  )

  if (
    (streetComponent && !hasNumericRange.test(streetComponent.short_name)) ||
    (!streetComponent &&
      routeComponent &&
      hasHighwayName.test(routeComponent.short_name))
  ) {
    return recommendedLocation
  } else if (!streetComponent && routeComponent) {
    const rangeInterpolatedLocation = getNearSameNameRoute(
      allLocations,
      routeComponent,
      GEO_LOCATION_TYPES.RANGE_INTERPOLATED
    )
    const roofTopLocation = getNearSameNameRoute(
      allLocations,
      routeComponent,
      GEO_LOCATION_TYPES.ROOFTOP
    )
    return roofTopLocation || rangeInterpolatedLocation
  }
}

export const parseLocation = (
  location: GeoLocation,
  coords: GeoCoordinates
): GeoLocation => {
  if (!location) {
    throw new Error('Could not fetch location')
  }

  const addressComponents = location.address_components
    .filter(
      (item) =>
        item.types.indexOf('route') > -1 &&
        (hasUnnamedRoad.test(item.short_name) ||
          hasUnnamedRoad.test(item.long_name))
    )
    .map((unnamedRouteComponent) => ({
      ...unnamedRouteComponent,
      short_name: unnamedRouteComponent.short_name.replace(hasUnnamedRoad, ''),
      long_name: unnamedRouteComponent.long_name.replace(hasUnnamedRoad, ''),
    }))

  location.address_components[0] =
    addressComponents[0] || location.address_components[0]
  location.formatted_address = location.formatted_address?.replace(
    hasUnnamedRoad,
    ''
  )
  location.geometry.location.accuracy = coords.accuracy

  return Object.assign({}, location)
}

export const guessDefaultLocation = (allLocations: Array<GeoLocation>): GeoLocation => {
  const allLocationTypes = [
    GEO_LOCATION_TYPES.ROOFTOP,
    GEO_LOCATION_TYPES.RANGE_INTERPOLATED
  ]

  const locationsByType = allLocationTypes.reduce((locations: Array<GeoLocation>, locationType) =>
    [
      ...locations,
      ...getLocationsByType(allLocations, locationType)
    ], [])

  return locationsByType[0]
}

export const getLocationFromResults = (results: GeoLocation[], coords: GeoCoordinates): GeoLocation => {
  return guessLocationFromCoordsResults(results, coords);
}

export const guessLocationFromCoordsResults = (
  results: GeoLocation[],
  coords: GeoCoordinates
): GeoLocation => {
  const recommendedLocation = getRecommendedLocation(
    results,
    GEO_LOCATION_TYPES.GEOMETRIC_CENTER
  )
  const defaultLocation = guessDefaultLocation(results)
  const plusCodeExcluded = results.filter((location) => !location.types?.includes(GEO_ADDRESS_COMPONENT_TYPES.PLUS_CODE))

  const bestGuess =
    guessNearestLocation(results, recommendedLocation)
    || guessNearestLocation(results, defaultLocation)
    || recommendedLocation
    || plusCodeExcluded[0]
    || results[0]

  return parseLocation(bestGuess, coords)
}

export const requiresGpsPrompt = (permissionState: PERMISSION_STATE) =>
  [PERMISSION_STATE.UNKNOWN, PERMISSION_STATE.PROMPT].includes(permissionState)

export const __TEST__ = {
  getAddressComponentByType,
  getNearSameNameRoute,
}
